/* import __COLOCATED_TEMPLATE__ from './fin-phone-behavior.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import {
  type FinVoiceVoice,
  DEFAULT_VOICE,
  FIN_VOICE_11LABS_VOICES,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import {
  DEFAULT_VOICE_LANGUAGE,
  type FinVoiceVoiceLanguage,
  FIN_VOICE_VOICE_LANGUAGE_OPTIONS,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateFinBehavior: (behavior: {
      voice: FinVoiceVoice;
      greeting: string;
      greetingOutOfOfficeHours?: string;
      language: FinVoiceVoiceLanguage;
    }) => Promise<void>;
  };
}

export default class FinPhoneBehavior extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;

  @tracked voice: FinVoiceVoice = DEFAULT_VOICE;
  @tracked selectedLanguage: FinVoiceVoiceLanguage = DEFAULT_VOICE_LANGUAGE;
  @tracked greeting = '';
  @tracked greetingOutOfOfficeHours = '';
  @tracked sameGreetingForOutOfOfficeHours = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.resetFinBehavior();
  }

  get savingDisabled() {
    return Boolean(
      !this.hasChanges ||
        this.numberConfiguration?.isSaving ||
        (!this.sameGreetingForOutOfOfficeHours && !this.greetingOutOfOfficeHours),
    );
  }

  get hasChanges() {
    return (
      this.voice !== this.savedVoice ||
      this.greeting !== this.savedGreeting ||
      this.greetingOutOfOfficeHours !== this.savedGreetingOutOfOfficeHours ||
      this.selectedLanguage !== this.savedLanguage
    );
  }

  get defaultVoice(): FinVoiceVoice {
    if (this.appService.app.canUseFinVoiceDefaultVoiceLydia) {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      return 'Lydia';
    }
    return DEFAULT_VOICE;
  }

  get errorMessage() {
    if (!this.hasChanges) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-changes',
      );
    }

    if (!this.sameGreetingForOutOfOfficeHours && !this.greetingOutOfOfficeHours) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-greeting-out-of-office-hours',
      );
    }

    return undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get showOutOfOfficeHoursGreeting() {
    return this.numberConfiguration?.workspacePhoneNumberType !== 'ivr';
  }

  get canSetupFinVoicePhoneEasySetup(): boolean {
    return this.appService.app.canSetupFinVoicePhoneEasySetup;
  }

  get savedVoice() {
    let savedVoice = this.numberConfiguration?.voice;

    if (!savedVoice || !FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === savedVoice)) {
      return this.defaultVoice;
    }

    return savedVoice;
  }

  get savedLanguage() {
    if (this.numberConfiguration?.workspacePhoneNumberType === 'ivr') {
      return this.numberConfiguration?.ivrLanguage || DEFAULT_VOICE_LANGUAGE;
    }

    // Get language from phone number, fallback to default
    let phoneNumberId = this.numberConfiguration?.workspacePhoneNumberId;
    let phoneNumberData = phoneNumberId
      ? this.finVoiceConfigService.phoneNumbers[phoneNumberId]
      : undefined;

    return (phoneNumberData?.locale as FinVoiceVoiceLanguage) || DEFAULT_VOICE_LANGUAGE;
  }

  get savedGreeting() {
    return this.numberConfiguration?.greeting;
  }

  get savedGreetingOutOfOfficeHours() {
    return this.numberConfiguration?.greetingOutOfOfficeHours;
  }

  get activeVoiceLabel() {
    let savedVoice = this.savedVoice;
    let savedLanguage = this.savedLanguage;

    let voiceData = FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === savedVoice);
    let savedVoiceText = voiceData?.text || '';

    let languageData = FIN_VOICE_VOICE_LANGUAGE_OPTIONS.find(
      (language) => language.value === savedLanguage,
    );
    let savedLanguageText = languageData?.text ? this.intl.t(languageData.text) : '';

    return [savedLanguageText, savedVoiceText].join(' • ');
  }

  @action
  resetFinBehavior() {
    this.selectedLanguage = this.savedLanguage;
    this.voice = this.savedVoice;
    this.greeting = this.savedGreeting || '';
    this.greetingOutOfOfficeHours = this.savedGreetingOutOfOfficeHours || '';
    this.sameGreetingForOutOfOfficeHours = !this.greetingOutOfOfficeHours;
  }

  @action
  onVoiceLanguageChange(voice: FinVoiceVoice, language: FinVoiceVoiceLanguage) {
    this.voice = voice;
    this.selectedLanguage = language;
  }

  @action
  updateGreeting(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.greeting = target.value;
  }

  @action
  updateGreetingOutOfOfficeHours(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.greetingOutOfOfficeHours = target.value;
  }

  @action
  toggleUseGreetingOutOfOfficeHours() {
    this.sameGreetingForOutOfOfficeHours = !this.sameGreetingForOutOfOfficeHours;

    if (this.sameGreetingForOutOfOfficeHours) {
      this.greetingOutOfOfficeHours = '';
    }
  }

  @action
  async updateFinBehavior() {
    if (this.savingDisabled) {
      return;
    }

    await this.args.updateFinBehavior({
      voice: this.voice,
      greeting: this.greeting,
      greetingOutOfOfficeHours: this.greetingOutOfOfficeHours,
      language: this.selectedLanguage,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::FinPhoneBehavior': typeof FinPhoneBehavior;
  }
}
